import { useCallback, useSyncExternalStore } from 'react'

/**
 * Совпадает ли медиазапрос прямо сейчас.
 *
 * Нужен там, где от ширины зависит **семантика**, а не оформление: ниже 1024px колонка
 * навигации становится диалогом с ловушкой фокуса, а оглавление страницы — раскрывающимся
 * блоком. CSS такое переложить умеет, снять роль — нет.
 *
 * Через `useSyncExternalStore`, а не `useState` плюс эффект: порог мог быть пересечён между
 * первым рендером и подпиской (поворот телефона в момент загрузки), и React перечитывает
 * снимок сам — без синхронного `setState` в теле эффекта и лишнего каскада.
 *
 * Без `matchMedia` (jsdom, экзотический WebView) ответ — `false`. Это не «неизвестно»,
 * а mobile first: запросы в проекте пишутся через `min-width`, и несовпадение оставляет
 * узкую раскладку, которая работает всегда.
 *
 * Вынесен на третьем потребителе (правило 9): `AppShell`, `PageAnchorNav` и стенд каркаса.
 * Копии успели разойтись — в одной потерялась проверка `typeof window`.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return () => {}
      }

      const list = window.matchMedia(query)
      list.addEventListener('change', onChange)
      return () => list.removeEventListener('change', onChange)
    },
    [query],
  )

  const read = useCallback((): boolean => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
    return window.matchMedia(query).matches
  }, [query])

  // Серверного снимка нет отдельного: на сервере ширины не существует, и ответ тот же
  return useSyncExternalStore(subscribe, read, read)
}
